import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import "../../common/commonStyles.css";
import { isSubFieldVisible } from "../../Settings/productFormSubFieldUtils";

const BoxPricingTable = ({
  formData,
  handleChange,
  handleBlur,
  errors,
  selectedOption,
  accessToken,
  visibleSubFields = null,
}) => {
  const showBand = (bandNumber) =>
    isSubFieldVisible(visibleSubFields, `band_${bandNumber}`);
  const showPriceBox = isSubFieldVisible(visibleSubFields, "price_box");
  const showSpecialBox =
    visibleSubFields != null
      ? visibleSubFields.special_price_box === true
      : accessToken === "rhp";
  const showPricePiece = isSubFieldVisible(visibleSubFields, "price_piece");
  const showSpecialPiece =
    visibleSubFields != null
      ? visibleSubFields.special_price_piece === true
      : accessToken === "rhp";

  const bandConfig = useMemo(
    () =>
      [
        {
          bandNumber: 1,
          boxPriceField: "price",
          boxSpecialPriceField: "band1_special_box_price",
          piecePriceField: "split_price",
          pieceSpecialPriceField: "band1_special_piece_price",
          boxPriceError: errors.price,
          piecePriceError: errors.split_price,
        },
        {
          bandNumber: 2,
          boxPriceField: "price_1",
          boxSpecialPriceField: "band2_special_box_price",
          piecePriceField: "piece_price1",
          pieceSpecialPriceField: "band2_special_piece_price",
          boxPriceError: errors.price_1,
          piecePriceError: errors.piece_price1,
        },
        {
          bandNumber: 3,
          boxPriceField: "price_2",
          boxSpecialPriceField: "band3_special_box_price",
          piecePriceField: "piece_price2",
          pieceSpecialPriceField: "band3_special_piece_price",
          boxPriceError: errors.price_2,
          piecePriceError: errors.piece_price2,
        },
        {
          bandNumber: 4,
          boxPriceField: "price_3",
          boxSpecialPriceField: "band4_special_box_price",
          piecePriceField: "piece_price3",
          pieceSpecialPriceField: "band4_special_piece_price",
          boxPriceError: errors.price_3,
          piecePriceError: errors.piece_price3,
        },
        {
          bandNumber: 5,
          boxPriceField: "price_4",
          boxSpecialPriceField: "band5_special_box_price",
          piecePriceField: "piece_price4",
          pieceSpecialPriceField: "band5_special_piece_price",
          boxPriceError: errors.price_4,
          piecePriceError: errors.piece_price4,
        },
        {
          bandNumber: 6,
          boxPriceField: "price_5",
          boxSpecialPriceField: "band6_special_box_price",
          piecePriceField: "piece_price5",
          pieceSpecialPriceField: "band6_special_piece_price",
          boxPriceError: errors.price_5,
          piecePriceError: errors.piece_price5,
        },
      ].filter((band) => showBand(band.bandNumber)),
    [errors, visibleSubFields],
  );

  const showPieceColumns =
    selectedOption == "2" && (showPricePiece || showSpecialPiece);
  const showSpecialBoxColumn = showSpecialBox;
  const showSpecialPieceColumn = showSpecialPiece && selectedOption == "2";

  if (
    !bandConfig.length ||
    (!showPriceBox && !showSpecialBoxColumn && !showPieceColumns)
  ) {
    return null;
  }

  return (
    <TableContainer
      sx={{
        border: "1px solid rgba(145, 158, 171, 0.16)",
        borderRadius: 1,
        mt: 2,
      }}
    >
      <Table size="small" className="table-cell-border">
        <TableHead>
          <TableRow className="table-cell-header">
            <TableCell
              sx={{
                color: "#637387",
                fontWeight: 600,
                fontSize: 12,
                width: showPieceColumns ? "15%" : "25%",
              }}
            >
              {accessToken === "rhp" ? "Band" : "Price"}
            </TableCell>
            {showPriceBox && (
              <TableCell
                sx={{
                  color: "#637387",
                  fontWeight: 600,
                  fontSize: 12,
                  width: showPieceColumns ? "21%" : "37.5%",
                }}
              >
                Price (Box) <span style={{ color: "red" }}>*</span>
              </TableCell>
            )}
            {showSpecialBoxColumn && (
              <TableCell
                sx={{
                  color: "#637387",
                  fontWeight: 600,
                  fontSize: 12,
                  width: showPieceColumns ? "21%" : "37.5%",
                }}
              >
                Special Price (Box)
              </TableCell>
            )}
            {selectedOption == "2" && showPricePiece && (
              <TableCell
                sx={{
                  color: "#637387",
                  fontWeight: 600,
                  fontSize: 12,
                  width: "21%",
                }}
              >
                Price (Piece) <span style={{ color: "red" }}>*</span>
              </TableCell>
            )}
            {showSpecialPieceColumn && (
              <TableCell
                sx={{
                  color: "#637387",
                  fontWeight: 600,
                  fontSize: 12,
                  width: "22%",
                }}
              >
                Special Price (Piece)
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {bandConfig.map((band) => (
            <TableRow
              key={band.bandNumber}
              sx={{ "&:hover": { bgcolor: "rgba(145, 158, 171, 0.08)" } }}
            >
              <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
                {accessToken === "rhp"
                  ? `Price ${band.bandNumber}`
                  : `Price ${band.bandNumber}`}
              </TableCell>
              {showPriceBox && (
                <TableCell sx={{ p: 1 }}>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    size="small"
                    type="number"
                    name={band.boxPriceField}
                    value={formData[band.boxPriceField] || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={(e) => e.target.blur()}
                    error={!!band.boxPriceError}
                    sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                  />
                </TableCell>
              )}
              {showSpecialBoxColumn && (
                <TableCell sx={{ p: 1 }}>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    size="small"
                    type="number"
                    name={band.boxSpecialPriceField}
                    value={formData[band.boxSpecialPriceField] || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={(e) => e.target.blur()}
                    sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                  />
                </TableCell>
              )}
              {selectedOption == "2" && showPricePiece && (
                <TableCell sx={{ p: 1 }}>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    size="small"
                    type="number"
                    name={band.piecePriceField}
                    value={formData[band.piecePriceField] || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={(e) => e.target.blur()}
                    error={!!band.piecePriceError}
                    sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
                  />
                </TableCell>
              )}
              {showSpecialPieceColumn && (
                <TableCell sx={{ p: 1 }}>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    size="small"
                    type="number"
                    name={band.pieceSpecialPriceField}
                    value={formData[band.pieceSpecialPriceField] || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={(e) => e.target.blur()}
                    sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BoxPricingTable;
